import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';

interface PlanLimits {
  invoicesPerMonth: number;
  clientsMax: number;
  aiCapturesPerMonth: number;
  calendarSync: boolean;
  analyticsAdvanced: boolean;
  partnersEnabled: boolean;
  exportGestora: boolean;
  customTemplates: boolean;
  invoiceBranding: boolean;
  googleDriveSync: boolean;
  fiscalBreakdown?: boolean;
}

interface PlanUsage {
  invoicesThisMonth: number;
  invoicesRemaining: number;
  clientsTotal: number;
  clientsRemaining: number;
  aiCapturesThisMonth: number;
  aiCapturesRemaining: number;
}

interface UsePlanReturn {
  plan: 'starter' | 'pro' | null;
  isPro: boolean;
  isStarter: boolean;
  limits: PlanLimits | null;
  usage: PlanUsage | null;
  isLoading: boolean;
  canCreateInvoice: boolean;
  canCreateClient: boolean;
  canUseAiCapture: boolean;
  refreshPlan: () => void;
}

const CACHE_TTL = 60000;

export const usePlan = (): UsePlanReturn => {
  const { user, token } = useAuth();
  const CACHE_KEY = `guideme_plan_usage_${user?.id ?? 'anon'}`;
  const [limits, setLimits] = useState<PlanLimits | null>(null);
  const [usage, setUsage] = useState<PlanUsage | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const plan = (user?.plan as 'starter' | 'pro') || null;
  const isPro = plan === 'pro' || user?.permisos === 'superadmin' || user?.permisos === 'administrador';
  const isStarter = plan === 'starter' && !isPro;

  const fetchPlanUsage = useCallback(async () => {
    if (!token) return;

    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const { data, ts } = JSON.parse(cached);
        if (Date.now() - ts < CACHE_TTL) {
          setLimits(data.plan_limits);
          setUsage({
            invoicesThisMonth: data.invoicesThisMonth,
            invoicesRemaining: data.invoicesRemaining,
            clientsTotal: data.clientsTotal,
            clientsRemaining: data.clientsRemaining,
            aiCapturesThisMonth: data.aiCapturesThisMonth ?? 0,
            aiCapturesRemaining: data.aiCapturesRemaining ?? -1
          });
          setIsLoading(false);
          return;
        }
      } catch {}
    }

    try {
      const res = await fetch(buildApiUrl('/api/user/plan-usage'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const { data } = await res.json();
        setLimits(data.plan_limits);
        setUsage({
          invoicesThisMonth: data.invoicesThisMonth,
          invoicesRemaining: data.invoicesRemaining,
          clientsTotal: data.clientsTotal,
          clientsRemaining: data.clientsRemaining,
          aiCapturesThisMonth: data.aiCapturesThisMonth ?? 0,
          aiCapturesRemaining: data.aiCapturesRemaining ?? -1
        });
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
      }
    } catch (err) {
      console.error('Error fetching plan usage:', err);
    } finally {
      setIsLoading(false);
    }
  }, [token, CACHE_KEY]);

  const refreshPlan = useCallback(() => {
    sessionStorage.removeItem(CACHE_KEY);
    setIsLoading(true);
    fetchPlanUsage();
  }, [fetchPlanUsage]);

  useEffect(() => {
    fetchPlanUsage();
  }, [fetchPlanUsage]);

  const canCreateInvoice = isPro || (usage?.invoicesRemaining ?? 1) > 0;
  const canCreateClient = isPro || (usage?.clientsRemaining ?? 1) > 0;
  const canUseAiCapture = isPro || (usage?.aiCapturesRemaining ?? 1) !== 0;

  return {
    plan, isPro, isStarter, limits, usage, isLoading,
    canCreateInvoice, canCreateClient, canUseAiCapture, refreshPlan
  };
};
